import { useEffect, useRef, useState } from 'react';
import api from '../../services/api';

/**
 * Watch for a new app or plugin to register in this space, and say which one.
 *
 * The assistant does the work in a terminal; New app and onboarding both wait
 * for its `push` to land. Returns the arrival (a plugin carries `is_plugin`),
 * or null while waiting. `onArrive` is called once, when it lands.
 *
 * BASELINED AT MOUNT, deliberately. Somebody who already owns four apps can
 * still start a new one — and a poll that simply asks "are there any apps?"
 * would answer yes on the first tick and announce an app they registered last
 * March as having just arrived. Only an id that was not there when the watch
 * began counts as arriving. A new space is a new baseline.
 *
 * It only runs while the page is visible, because a background tab asking
 * every fifteen seconds forever is somebody's battery, and it stops the moment
 * something lands.
 */
export default function useArrival(groupId, onArrive) {
  const [arrived, setArrived] = useState(null);
  const onArriveRef = useRef(onArrive);
  onArriveRef.current = onArrive;

  useEffect(() => {
    if (arrived) return undefined;
    let stop = false;
    let before = null;
    const tick = async () => {
      if (document.hidden) return;
      try {
        // Plugins are read from their own list as well as my-apps, so a plugin
        // registering is seen whichever list carries it; an id in both is one arrival.
        const [res, plugins] = await Promise.all([
          api.request(`/third-party/my-apps${groupId ? `?group_id=${groupId}` : ''}`),
          api.getMyPlugins(groupId || undefined).catch(() => []),
        ]);
        const apps = [...(res?.apps || []), ...(plugins || []).map(p => ({ ...p, is_plugin: true }))];
        if (stop) return;
        if (before === null) {
          before = new Set(apps.map(a => String(a.id)));
          return;
        }
        const fresh = apps.find(a => !before.has(String(a.id)));
        if (fresh) {
          setArrived(fresh);
          onArriveRef.current?.(fresh);
        }
      } catch { /* the page is still useful without this */ }
    };
    // Establish the baseline straight away rather than waiting out the first
    // interval, or an app registered in the next fifteen seconds is missed.
    tick();
    const id = setInterval(tick, 15000);
    return () => { stop = true; clearInterval(id); };
  }, [arrived, groupId]);

  return arrived;
}
